import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { useGuardian } from '../contexts/GuardianContext';

const { FiMail, FiSend, FiUser, FiFileText, FiCheckCircle } = FiIcons;

const TEMPLATES = [
  {
    id: 'intro',
    name: 'Initial Introduction',
    subject: '[Year] [Position] – [First Last] | TheNextlete Profile',
    body: `Dear Coach [Last Name],

My name is [First Name], and I’m a [Year] [Position] at [High School] in [City, State]. I’m interested in [School Name] because [1 specific reason: academic program / style of play / recent result / culture].

My student-athlete resume (GPA: [GPA], measurables, stats, and highlight video) is here:
TheNextlete: [Profile Link]

Are you currently recruiting [Year]s at [Position]? If so, what is the best way for me to stay on your radar and learn more about your recruiting process for my class (questionnaire, camp, call, etc.)?

Thank you for your time,
[First Last]
[Phone] | [Email]
[High School], Class of [Year] | [Club Team]
[City, State]`
  },
  {
    id: 'update',
    name: 'Highlight/Stats Update',
    subject: 'Update: [Year] [Position] – New Highlights + [Key Stat/Metric]',
    body: `Dear Coach [Last Name],

I’m [First Name], a [Year] [Position] from [High School] ([City, State]). I wanted to send a quick update with my new highlight film and recent results from [season/team].

Recent updates:

Team result: [Record / playoff finish / tournament result]
My impact: [Key stat line or achievement—keep to 1 line]
New/Improved metric: [measurable, ranking, award, or academic update]
Upcoming schedule: [1 line—where/when you’ll be playing next, if applicable]

TheNextlete profile (video + stats + academics): [Profile Link]

If you’re still recruiting for [Position] in the [Year] class, I’d appreciate any feedback on where I fit on your recruiting board and what the best next step is (questionnaire, camp, call, sending full game film, etc.).

Thank you for your time,
[First Last]
[Phone] | [Email]
[High School], Class of [Year] | [Club Team]
[City, State]`
  },
  {
    id: 'camp',
    name: 'Camp Attendance',
    subject: 'Registered for [School Name] Prospect Camp ([Date]) – [Year] [Position] [First Last]',
    body: `Dear Coach [Last Name],

I’m [First Name], a [Year] [Position] from [High School] ([City, State]) and [Club Team]. I wanted to let you know I’m registered for your [Camp Name] Prospect Camp on [Date] ([Session/Time, if applicable]) and I’m excited for the chance to compete in front of your staff and learn more about the program.

Quick info for camp:

Position: [Position] | Jersey #: [#]
Height/Weight: [Height] / [Weight]
Notable recent metric/achievement: [1 line]

TheNextlete profile (video + stats + academics): [Profile Link]

Is there anything you’d like me to bring or do ahead of time (questionnaire, transcripts, full game film)? Also, what’s the best way to check in with your staff when I arrive?

Thank you, and I look forward to meeting you at camp,
[First Last]
[Phone] | [Email]`
  }
];

function EmailOutreach({ contacts, schools, onLogAdded }) {
  const { getActiveUserId } = useGuardian();
  const [selectedContact, setSelectedContact] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [logging, setLogging] = useState(false);
  const [success, setSuccess] = useState(false);
  const [profile, setProfile] = useState({});

  // Fetch the latest profile data directly from Supabase
  useEffect(() => {
    const fetchProfile = async () => {
      const activeUserId = getActiveUserId();
      if (!activeUserId) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', activeUserId)
          .single();
        
        if (data && !error) {
          setProfile(data);
        }
      } catch (err) {
        console.error('Error fetching profile for email outreach:', err);
      }
    };

    fetchProfile();
  }, [getActiveUserId()]);

  // Re-apply the template whenever the template, contact, or profile changes
  useEffect(() => {
    if (selectedTemplate) {
      applyTemplate(selectedTemplate);
    }
  }, [selectedTemplate, selectedContact, profile]);

  const applyTemplate = (templateId) => {
    const template = TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    let contact = null;
    let school = null;

    if (selectedContact) {
      contact = contacts.find(c => c.id === selectedContact);
      if (contact && contact.saved_school_id) {
        school = schools.find(s => s.saved_id === contact.saved_school_id);
      }
    }

    const firstName = profile.first_name || '';
    const lastName = profile.last_name || '';
    const myName = firstName || lastName ? `${firstName} ${lastName}`.trim() : '[First Last]';
    
    let locationStr = '[City, State]';
    if (profile.city && profile.state) {
      locationStr = `${profile.city}, ${profile.state}`;
    } else if (profile.city || profile.state) {
      locationStr = profile.city || profile.state;
    } else if (profile.location) {
      locationStr = profile.location;
    }

    const profileLink = profile.username ? `https://thenextlete.com/${profile.username}` : `https://thenextlete.com/resume/preview`;

    const replacements = {
      '[My Name]': myName,
      '[First Last]': myName,
      '[First Name]': firstName || '[First Name]',
      '[Year]': profile.graduation_year || '[Year]',
      '[Position]': profile.position || '[Position]',
      '[High School]': profile.high_school || '[High School]',
      '[City, State]': locationStr,
      '[Phone Number]': profile.phone || '[Phone]',
      '[Phone]': profile.phone || '[Phone]',
      '[Email]': profile.email || '[Email]',
      '[GPA]': profile.gpa || '[GPA]',
      '[Height]': profile.height || '[Height]',
      '[Weight]': profile.weight || '[Weight]',
      '[Last Name]': contact && contact.last_name ? contact.last_name : '[Last Name]',
      '[School Name]': school && school.name ? school.name : '[School Name]',
      '[Profile Link]': profileLink,
      '[Club Team]': profile.club_team || '[Club Team]'
    };

    let newSubject = template.subject;
    let newBody = template.body;

    Object.keys(replacements).forEach(key => {
      newSubject = newSubject.replaceAll(key, replacements[key]);
      newBody = newBody.replaceAll(key, replacements[key]);
    });

    setSubject(newSubject);
    setBody(newBody);
  };

  const handleLogAndSend = async () => {
    if (!selectedContact) {
      alert('Please select a contact to email.');
      return;
    }
    const contact = contacts.find(c => c.id === selectedContact);
    if (!contact || !contact.email) {
      alert('Selected contact does not have an email address.');
      return;
    }

    try {
      setLogging(true);
      const activeUserId = getActiveUserId();
      
      // 1. Log the communication
      const { error: logError } = await supabase.from('communication_log').insert({
        user_id: activeUserId,
        contact_id: contact.id,
        type: 'email',
        outcome: 'sent',
        details: { subject, body }
      });
      if (logError) throw logError;

      // 2. Update contact last_contacted_at
      await supabase.from('contacts').update({
        last_contacted_at: new Date().toISOString()
      }).eq('id', contact.id);

      // 3. Open mail client
      const mailtoLink = `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      if(onLogAdded) onLogAdded();

    } catch (err) {
      console.error('Failed to log email:', err);
      alert('Failed to log email. Please try again.');
    } finally {
      setLogging(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiMail} className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-space-grotesk font-semibold text-secondary">Email Outreach</h3>
          <p className="text-sm text-gray-500">Draft and log emails to college coaches</p>
        </div>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2 text-green-800">
          <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
          <span>Email logged successfully! Opening your email client...</span>
        </div>
      )}

      <div className="space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Contact</label>
            <div className="relative">
              <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select 
                value={selectedContact} 
                onChange={(e) => setSelectedContact(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary outline-none appearance-none"
              >
                <option value="">-- Select a Contact --</option>
                {contacts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name} ({c.title || 'Coach'})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Template</label>
            <div className="relative">
              <SafeIcon icon={FiFileText} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select 
                value={selectedTemplate} 
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary outline-none appearance-none"
              >
                <option value="">-- Select Template --</option>
                {TEMPLATES.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
          <input 
            type="text" 
            value={subject} 
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Message Body</label>
          <textarea 
            value={body} 
            onChange={(e) => setBody(e.target.value)}
            rows={15}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary outline-none resize-y font-sans"
          />
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button 
            onClick={handleLogAndSend} 
            disabled={logging || !selectedContact || !subject || !body}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 font-medium disabled:opacity-50"
          >
            <SafeIcon icon={FiSend} className="w-4 h-4" />
            <span>{logging ? 'Logging...' : 'Log & Send via Email Client'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailOutreach;